import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { TokenHolder, type TokenStorage } from './token';

export type DiaClient = SupabaseClient<Database>;

export type ClientConfig = {
  url: string;
  /** The anon key. Safe in the bundle only because RLS is complete (Section 12). */
  anonKey: string;
  /**
   * The session JWT minted by guest-login or staff-login. Guests and crew are
   * not Supabase Auth users, so the token is passed as a header rather than
   * held in the auth client.
   */
  accessToken?: string | null;
  /** Where supabase-js keeps an owner's own Supabase Auth session. */
  storageKey?: string;
  /** Where the minted session token is kept, so a reload starts signed in. */
  tokenKey?: string;
  /** Test seam. Defaults to localStorage, or memory where there is none. */
  tokenStorage?: TokenStorage;
  /** Test seam. Defaults to globalThis.fetch. */
  fetch?: typeof fetch;
};

/**
 * Every client made here, and the token it is carrying right now.
 *
 * A WeakMap rather than a field on the client, so nothing has to pretend to
 * know supabase-js's private shape, and a client that goes out of scope takes
 * its token with it.
 */
const holders = new WeakMap<DiaClient, TokenHolder>();

/**
 * The parts of a Supabase project that read our own session token.
 *
 * `/auth/v1/` is deliberately not here. That is Supabase Auth's own door, which
 * an owner signs in through with an email and a password, and it has to keep
 * carrying the anon key. Putting a guest's minted token on a sign in request
 * would break the one login that has nothing to do with the outbox.
 */
const SESSION_PATHS = ['/rest/v1/', '/storage/v1/', '/functions/v1/', '/realtime/v1/'];

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

export function carriesSessionToken(url: string): boolean {
  return SESSION_PATHS.some((path) => url.includes(path));
}

/**
 * One client per shell, made once at startup.
 *
 * The token is applied by a fetch wrapper rather than by mutating the header
 * object the client was constructed with. That was defect D2: supabase-js
 * copies `global.headers` into the REST and storage clients at construction, so
 * changing it after a login changed nothing and every signed in read and write
 * went out as anon and came back 401. A wrapper reads the current token on each
 * call instead, which also means a sign in, a sign out and an expiry all take
 * effect on the very next request with nothing to rebuild.
 *
 * Reads are network first with a cached fallback, which is a service worker
 * concern, not a client one. Writes never go straight through this client from
 * a screen: they go through the outbox (see sync.ts), so a dropout is normal
 * rather than a lost tick.
 */
export function createDiaClient({
  url,
  anonKey,
  accessToken,
  storageKey = 'dia.auth',
  tokenKey,
  tokenStorage,
  fetch: baseFetch,
}: ClientConfig): DiaClient {
  const holder = new TokenHolder({ key: tokenKey ?? `${storageKey}.token`, storage: tokenStorage });
  if (accessToken !== undefined) holder.set(accessToken);

  const send: typeof fetch = (input, init) =>
    (baseFetch ?? globalThis.fetch)(input as RequestInfo, init);

  const client = createClient<Database>(url, anonKey, {
    auth: {
      // Owners use Supabase Auth. Everyone else arrives with a minted JWT, so
      // there is no session to refresh in the background and no URL to parse.
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
      storageKey,
    },
    global: {
      headers: {},
      fetch: (input, init) => {
        const token = holder.get();
        if (!token || !carriesSessionToken(requestUrl(input))) return send(input, init);

        // supabase-js has already set Authorization to the anon key (or to an
        // owner's Supabase Auth token) by the time it calls us. Ours wins for
        // the people supabase-js has never heard of.
        const headers = new Headers(init?.headers);
        headers.set('Authorization', `Bearer ${token}`);
        if (!headers.has('apikey')) headers.set('apikey', anonKey);
        return send(input, { ...init, headers });
      },
    },
    db: { schema: 'public' },
  });

  holders.set(client, holder);
  return client;
}

/**
 * Swaps the token on an existing client after a login, a sign out or a refresh.
 * It is persisted, so the next app open starts signed in, and cleared the
 * moment it is set to null.
 */
export function setAccessToken(client: DiaClient, accessToken: string | null): void {
  const holder = holders.get(client);
  if (holder) {
    holder.set(accessToken);
    return;
  }

  // A client this module did not make (a test fake, or a bare createClient).
  // Fall back to the old header mutation so nothing breaks, and say so.
  const headers = (client as unknown as { headers?: Record<string, string> }).headers;
  if (!headers) return;
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  else delete headers.Authorization;
}

/** What this client would send right now. Null once the token has expired. */
export function getAccessToken(client: DiaClient): string | null {
  return holders.get(client)?.get() ?? null;
}

/**
 * The stored token, expired or not. An app asks this at boot: a token that is
 * past its expiry means sign the person out and say so, rather than letting
 * them tap around a screen whose every read is refused.
 */
export function storedAccessToken(client: DiaClient): string | null {
  return holders.get(client)?.peek() ?? null;
}

/** Fires whenever the token changes, including when it expires by itself. */
export function onAccessTokenChange(
  client: DiaClient,
  listener: (token: string | null) => void,
): () => void {
  return holders.get(client)?.subscribe(listener) ?? (() => {});
}
